/**
 * The engine rewrites text it has already sent. Smart formatting re-capitalises and punctuates a
 * segment once that segment goes final; comparing whole words with case and punctuation stripped
 * absorbs that.
 *
 * It also drops fillers and respells numbers ("um i need" -> "i need", "fifteen" -> "15"), which
 * changes the word count so that every later word shifts out of position and no longer lines up
 * with the word it replaces.
 */

const bareWord = (word: string) => word.toLowerCase().replace(/[^\p{L}\p{N}]/gu, "");

const words = (text: string) => [...text.matchAll(/\S+/g)];

function agreeingWordCount(left: RegExpExecArray[], right: RegExpExecArray[]): number {
  let agreeing = 0;

  while (
    agreeing < left.length &&
    agreeing < right.length &&
    bareWord(left[agreeing][0]) === bareWord(right[agreeing][0])
  ) {
    agreeing += 1;
  }

  return agreeing;
}

function endOfWord(wordList: RegExpExecArray[], count: number): number {
  const bounded = Math.min(count, wordList.length);
  if (bounded === 0) return 0;

  const last = wordList[bounded - 1];
  return last.index + last[0].length;
}

export interface ProvisionalTail {
  /** Pass back on the next transcript of the same session; a new session starts from 0. */
  settledWords: number;
  /** Trailing characters of `current` the engine is still revising. */
  length: number;
}

export function provisionalTail(previous: string, current: string, settledWords: number): ProvisionalTail {
  const currentWords = words(current);
  const agreeing = agreeingWordCount(words(previous), currentWords);
  // Only ever advances: an earlier word the engine drops or respells must not pull words the user
  // has already watched settle back into the tail. Bounded by the current transcript, which the
  // engine can make shorter than the one it replaces.
  const settled = Math.min(Math.max(agreeing, settledWords), currentWords.length);

  return { settledWords: settled, length: current.length - endOfWord(currentWords, settled) };
}
